import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTimer } from '../../contexts/TimerContext';
import {
  getAgeWeeks,
  getWakeWindow,
  getLastNapEndTime,
  WAKE_WINDOW_CUTOFF_WEEKS,
} from '../../utils/wakeWindowUtils';

/**
 * NapReminderBanner — amber dismissible banner shown when no daytime nap
 * has been logged in the last 48 hours for a child that still needs naps.
 *
 * Dismissed for the session only (no Firestore persistence needed).
 *
 * Props:
 *   dateOfBirth  {string}  YYYY-MM-DD or ISO — from selectedChild.dateOfBirth
 *   sleepData    {Array}   recent sleep entries from useRecentItems (already fetched)
 */
export default function NapReminderBanner({ dateOfBirth, sleepData }) {
  const [dismissed, setDismissed] = useState(false);
  const { timer } = useTimer();

  if (dismissed) return null;

  // Sleep timer is actively running — baby is sleeping right now, no reminder needed
  if (timer?.type === 'sleep') return null;

  const ageWeeks = getAgeWeeks(dateOfBirth);
  if (ageWeeks == null || ageWeeks >= WAKE_WINDOW_CUTOFF_WEEKS) return null;

  const wakeWindow = getWakeWindow(ageWeeks);
  if (!wakeWindow) return null;

  // A daytime nap is logged — no reminder needed
  if (getLastNapEndTime(sleepData)) return null;

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.75rem',
        background: '#fef3c7',
        border: '1px solid #f59e0b',
        borderLeft: '4px solid #d97706',
        borderRadius: '8px',
        padding: '0.6rem 0.875rem',
        marginBottom: '1rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>😴</span>
        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#92400e' }}>
          No nap logged in the last 48 hours
        </span>
        <span style={{ fontSize: '0.875rem', color: '#78350f' }}>
          — has baby slept?
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
        <Link
          to="/app/sleep"
          style={{
            fontSize: '0.8rem',
            fontWeight: 700,
            color: '#92400e',
            background: '#fde68a',
            padding: '0.25rem 0.65rem',
            borderRadius: '50px',
            textDecoration: 'none',
            border: '1px solid #d97706',
            whiteSpace: 'nowrap',
          }}
        >
          Log a nap
        </Link>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss reminder"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.8rem',
            color: '#92400e',
            padding: '0.1rem 0.25rem',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
